<script setup>
// NOOVI: Modal de import de cards via CSV (Gap C — fase-10).
// Importa para a pipeline atualmente aberta (pipelineId), chamando o endpoint
// namespaced pipeline/cards/import (admin-only no backend).
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from '../../../../components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import { downloadBlob } from 'dashboard/helper/downloadHelper';

const props = defineProps({
  pipelineId: { type: [Number, String], default: null },
});

const emit = defineEmits(['imported']);
const show = defineModel('show', { type: Boolean, default: false });
const { t } = useI18n();
const { showAlert } = useAlert();

const fileInput = ref(null);
const selectedFile = ref(null);
const isImporting = ref(false);
const isDownloadingTemplate = ref(false);
const fileError = ref('');
const requestError = ref('');
const isBusy = computed(() => isImporting.value || isDownloadingTemplate.value);

// Colunas aceitas pelo import (espelham DataImport::PipelineCardManager).
const templateColumns =
  'title, stage, description, expected_revenue, contact_identifier, contact_email, contact_phone';

const downloadTemplate = async () => {
  if (isBusy.value) return;

  isDownloadingTemplate.value = true;
  requestError.value = '';
  try {
    const { data } = await PipelineCardsAPI.downloadImportTemplate();
    downloadBlob('pipeline_cards_import_template.csv', data);
  } catch (error) {
    requestError.value =
      error?.response?.data?.error || t('PIPELINE.IMPORT.ERROR');
    showAlert(requestError.value, 'error');
  } finally {
    isDownloadingTemplate.value = false;
  }
};

const onFileChange = event => {
  const file = event.target?.files?.[0] || null;
  fileError.value = '';
  requestError.value = '';

  if (
    file &&
    !file.name.toLowerCase().endsWith('.csv') &&
    file.type !== 'text/csv'
  ) {
    selectedFile.value = null;
    fileError.value = t('PIPELINE_MODALS.IMPORT.FILE_INVALID');
    return;
  }

  selectedFile.value = file;
};

const reset = () => {
  selectedFile.value = null;
  fileError.value = '';
  requestError.value = '';
  if (fileInput.value) fileInput.value.value = null;
};

const onClose = () => {
  if (isBusy.value) return;
  reset();
  show.value = false;
};

const submit = async () => {
  if (isBusy.value) return;
  if (!selectedFile.value) {
    fileError.value = t('PIPELINE_MODALS.IMPORT.FILE_REQUIRED');
    fileInput.value?.focus();
    return;
  }
  if (!props.pipelineId) {
    requestError.value = t('PIPELINE.IMPORT.ERROR');
    return;
  }

  isImporting.value = true;
  requestError.value = '';
  try {
    await PipelineCardsAPI.importCards(props.pipelineId, selectedFile.value);
    showAlert(t('PIPELINE.IMPORT.SUCCESS'));
    emit('imported');
    reset();
    show.value = false;
  } catch (error) {
    requestError.value =
      error?.response?.data?.error || t('PIPELINE.IMPORT.ERROR');
    showAlert(requestError.value, 'error');
  } finally {
    isImporting.value = false;
  }
};

watch(show, visible => {
  if (!visible && !isBusy.value) reset();
});
</script>

<template>
  <Modal
    v-model:show="show"
    :close-on-backdrop-click="!isBusy"
    :close-on-escape="!isBusy"
    :show-close-button="!isBusy"
    initial-focus-selector="#pipeline-csv-file"
    @close="onClose"
  >
    <section
      class="flex max-w-lg flex-col gap-4 p-4 sm:p-6"
      :aria-busy="isBusy"
      data-modal="pipeline-card-import"
    >
      <h2 class="pr-10 text-base font-medium text-n-slate-12">
        {{ t('PIPELINE.IMPORT.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('PIPELINE.IMPORT.DESCRIPTION') }}
      </p>
      <div
        class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-1 border border-n-weak"
      >
        <div
          class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('PIPELINE.IMPORT.TEMPLATE_TITLE') }}
          </span>
          <Button
            variant="faded"
            color="slate"
            size="lg"
            icon="i-lucide-download"
            :label="t('PIPELINE.IMPORT.DOWNLOAD_TEMPLATE')"
            :is-loading="isDownloadingTemplate"
            :disabled="isBusy"
            class="w-full sm:w-auto"
            @click="downloadTemplate"
          />
        </div>
        <p class="text-xs text-n-slate-11">
          {{ t('PIPELINE.IMPORT.COLUMNS_HINT') }}
        </p>
        <code class="text-xs text-n-slate-12 break-words">{{
          templateColumns
        }}</code>
      </div>
      <div class="space-y-2">
        <span class="block text-sm font-medium text-n-slate-12">
          {{ t('PIPELINE_MODALS.IMPORT.FILE_LABEL') }}
        </span>
        <label
          for="pipeline-csv-file"
          class="flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-4 text-sm font-medium transition-colors focus-within:ring-2 focus-within:ring-n-brand"
          :class="
            fileError
              ? 'border-n-ruby-9 bg-n-ruby-3 text-n-ruby-11'
              : 'border-n-strong bg-n-alpha-1 text-n-slate-12 hover:bg-n-alpha-2'
          "
        >
          <fluent-icon icon="document" size="18" aria-hidden="true" />
          <span class="break-all text-center">
            {{ selectedFile?.name || t('PIPELINE_MODALS.IMPORT.FILE_ACTION') }}
          </span>
          <input
            id="pipeline-csv-file"
            ref="fileInput"
            type="file"
            name="pipeline-csv-file"
            accept=".csv,text/csv"
            class="sr-only"
            :disabled="isBusy"
            :aria-invalid="Boolean(fileError)"
            :aria-describedby="
              fileError ? 'pipeline-csv-file-error' : undefined
            "
            @change="onFileChange"
          />
        </label>
        <p class="text-xs text-n-slate-11">
          {{ t('PIPELINE_MODALS.IMPORT.FILE_HELP') }}
        </p>
        <p
          v-if="fileError"
          id="pipeline-csv-file-error"
          class="text-sm text-n-ruby-11"
          role="alert"
        >
          {{ fileError }}
        </p>
      </div>

      <p
        v-if="requestError"
        class="rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
        role="alert"
      >
        {{ requestError }}
      </p>

      <footer
        class="flex flex-col-reverse gap-2 border-t border-n-weak pt-4 sm:flex-row sm:justify-end"
      >
        <Button
          variant="ghost"
          color="slate"
          size="lg"
          :label="t('PIPELINE.IMPORT.CANCEL')"
          :disabled="isBusy"
          class="w-full sm:w-auto"
          @click="onClose"
        />
        <Button
          size="lg"
          :label="t('PIPELINE.IMPORT.SUBMIT')"
          :is-loading="isImporting"
          :disabled="isBusy"
          class="w-full sm:w-auto"
          @click="submit"
        />
      </footer>
    </section>
  </Modal>
</template>
